import { ICreative, useShareCreative } from '@/shared/api'
import { ShareSVGIcon } from '@/shared/assets'
import { Spinner, Toast } from '@/shared/ui'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/ui/tooltip'
import cl from 'clsx'
import { CSSProperties, ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'

import { LinkDescriptor } from '@synapex/shared2/entities'
import classes from './ShareCreative.module.scss'
import {
    TELEGRAM_CAPTION_LENGTH_LIMIT,
    TELEGRAM_TEXT_LENGTH_LIMIT,
} from '@synapex/shared2/telegram'

import { type MarkDataInput, makeMarkingData } from './marking'

/** Пропсы для render props children */
export interface ShareCreativeRenderProps {
    isLoading: boolean
}

/** Тип children: ReactNode или функция (render props) */
type ShareCreativeChildren =
    | ReactNode
    | ((props: ShareCreativeRenderProps) => ReactNode)

export const ShareCreative = ({
    children,
    withTooltip = false,
    highlight = false,
    toastStyle,
    disabled,
    marking,
    markableFile,
    workspace_type,
    link_id,
}: LinkDescriptor & {
    children?: ShareCreativeChildren
    // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
    withTooltip?: boolean | ReactNode
    highlight?: boolean
    marking?: MarkDataInput
    markableFile?: ICreative['markableFile']
    toastStyle?: CSSProperties
    disabled?: () => void
}) => {
    const [timer, setTimer] = useState<number>(0)
    const [toastMessage, setToastMessage] = useState<string>('')
    const [isPreparing, setIsPreparing] = useState(false)

    const { mutate, isPending } = useShareCreative()

    // Общее состояние загрузки: подготовка данных + запрос
    const isLoading = isPreparing || isPending

    // useRef для отслеживания последнего сообщения (защита от быстрых изменений в Safari)
    const lastMessageRef = useRef<string>('')

    // Мемоизированный колбэк для закрытия Toast (fix: двойные уведомления в Safari)
    const handleToastClose = useCallback(() => {
        setToastMessage('')
        lastMessageRef.current = ''
    }, [])

    // Безопасная установка сообщения с защитой от дублирования в Safari
    const setToastMessageSafe = useCallback((message: string) => {
        // Предотвращаем установку того же сообщения дважды подряд
        // Это помогает избежать двойных уведомлений в Safari
        if (message !== lastMessageRef.current) {
            lastMessageRef.current = message
            setToastMessage(message)
        }
    }, [])

    const handleShare = async () => {
        if (timer > 0) {
            setToastMessageSafe(`Подождите, пока не истечет таймер ${timer} сек.`)
            return
        }

        // Блокируем повторные клики во время загрузки
        if (isLoading) {
            return
        }

        setIsPreparing(true)

        try {
            const markingData = marking
                ? await makeMarkingData(marking, markableFile)
                : undefined

            mutate(
                {
                    workspace_type,
                    link_id,
                    marking: markingData,
                },
                {
                    onSettled: () => {
                        setIsPreparing(false)
                        setTimer(1)
                    },
                    onSuccess: (result) => {
                        if (result.status === 'OK') {
                            setToastMessageSafe('Креатив успешно отправлен')
                        } else if (result.status === 'CAPTION_LENGTH_LIMIT') {
                            toast.error(
                                `Длина текста креатива после маркировки и замены ссылок(${result.captionLength}) превышает лимит Телеграма(${TELEGRAM_CAPTION_LENGTH_LIMIT}). Пожалуйста сократите текст или нанесите маркировку на медиа.`
                            )
                        } else if (result.status === 'TEXT_LENGTH_LIMIT') {
                            toast.error(
                                `Длина текста креатива после маркировки и замены ссылок(${result.textLength}) превышает лимит Телеграма(${TELEGRAM_TEXT_LENGTH_LIMIT}). Пожалуйста сократите текст.`
                            )
                        } else if (result.status === 'PENDING') {
                            setToastMessageSafe(
                                'Креатив обрабатывается. Скоро будет доставлен'
                            )
                        }
                    },
                    onError: () => {
                        setToastMessageSafe(
                            'Ошибка отправки креатива. Попробуйте позже'
                        )
                    },
                }
            )
        } catch {
            setIsPreparing(false)
            setToastMessageSafe('Ошибка подготовки данных. Попробуйте позже')
        }
    }

    useEffect(() => {
        if (timer <= 0) return

        const interval = setInterval(() => {
            setTimer((prev) => {
                if (prev <= 1) {
                    clearInterval(interval)
                    return 0
                }
                return prev - 1
            })
        }, 1000)

        return () => clearInterval(interval)
    }, [timer])

    const Tag = disabled ? 'div' : 'button'

    // Рендеринг children с поддержкой render props
    const renderChildren = () => {
        // Если children - функция (render props), вызываем её с isLoading
        if (typeof children === 'function') {
            return children({ isLoading })
        }

        // Если есть children (ReactNode), показываем его как есть
        if (children) {
            return children
        }

        // Дефолтная иконка: показываем спиннер при загрузке
        if (isLoading) {
            return <Spinner size={18} strokeWidth={2} />
        }

        return <ShareSVGIcon className="fill-secondary" />
    }

    let main = (
        <Tag
            {...(Tag === 'button'
                ? {
                      type: 'button',
                      // eslint-disable-next-line @typescript-eslint/no-misused-promises
                      onClick: handleShare,
                  }
                : {
                      onClick: disabled,
                  })}
            className={cl(
                'box-border flex h-[30px] min-w-[26px] cursor-pointer items-center justify-center',
                classes.root,
                { [classes.highlight]: highlight },
                { [classes.disabled]: disabled }
            )}
        >
            {renderChildren()}
        </Tag>
    )

    if (withTooltip) {
        const content =
            typeof withTooltip === 'boolean' ? (
                <p className="max-w-48">
                    Чтобы отправить пост, необходимо нажать чекбокс токен
                    получен
                </p>
            ) : (
                withTooltip
            )
        main = (
            <Tooltip>
                <TooltipTrigger asChild>{main}</TooltipTrigger>
                <TooltipContent>{content}</TooltipContent>
            </Tooltip>
        )
    }

    return (
        <>
            {main}
            <Toast
                message={toastMessage}
                visible={!!toastMessage}
                onClose={handleToastClose}
                greenSwooshIcon={true}
                style={toastStyle}
            />
        </>
    )
}
